import { useEffect, useMemo, useState } from 'react'
import { PageHeader, DataTable, StatusBadge, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { useApiList } from '../../hooks/use-api-list'
import { useAuth } from '../../hooks/use-auth'
import { api } from '../../services/api'
import type { UserRole } from '@alpha-edge/shared'

type ErrorType = 'parse_error' | 'rejected_message' | 'comm_failure' | 'config_error' | ''

type TimeRange = '1h' | '24h' | '7d' | ''

interface InstrumentError {
  readonly id: string
  readonly organizationId: string
  readonly organizationName: string | null
  readonly organizationIsArchived: boolean | null
  readonly siteId: string | null
  readonly siteName: string | null
  readonly deviceId: string | null
  readonly deviceName: string | null
  readonly udmDeviceId: number | null
  readonly udmSiteId: number | null
  readonly errorType: string
  readonly message: string
  readonly udmVersion: string | null
  readonly reportedAt: string
  readonly createdAt: string
}

interface CustomerOption {
  readonly id: string
  readonly name: string
}

function errorTypeBadgeStatus(errorType: string): string {
  switch (errorType) {
    case 'comm_failure':
      return 'critical'
    case 'config_error':
      return 'warning'
    case 'parse_error':
      return 'info'
    case 'rejected_message':
      return 'info'
    default:
      return 'inactive'
  }
}

function errorTypeLabel(errorType: string): string {
  switch (errorType) {
    case 'parse_error':
      return 'Parse Error'
    case 'rejected_message':
      return 'Rejected Message'
    case 'comm_failure':
      return 'Comm Failure'
    case 'config_error':
      return 'Config Error'
    default:
      return errorType
  }
}

function timeRangeToFrom(range: TimeRange): string | undefined {
  if (!range) return undefined
  const now = Date.now()
  const ms = range === '1h' ? 60 * 60 * 1000 : range === '24h' ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000
  return new Date(now - ms).toISOString()
}

export function InstrumentErrorsPage() {
  const { user } = useAuth()
  const role = user?.role as UserRole | undefined
  const isMedflow = role === 'medflow_admin'

  const [errorTypeFilter, setErrorTypeFilter] = useState<ErrorType>('')
  const [timeRange, setTimeRange] = useState<TimeRange>('24h')
  // medflow-only filters. Empty string means "any" -- omitted from API call.
  const [orgFilter, setOrgFilter] = useState('')
  const [siteFilter, setSiteFilter] = useState('') // matches sites.id (UUID) or udm_site_id
  const [deviceFilter, setDeviceFilter] = useState('') // matches devices.id (UUID) or udm_device_id
  const [customers, setCustomers] = useState<readonly CustomerOption[]>([])

  // Load customer list for the org filter dropdown (medflow_admin only).
  useEffect(() => {
    if (!isMedflow) return
    let cancelled = false
    api
      .get<readonly CustomerOption[]>('/fleet/customers')
      .then((res) => {
        if (!cancelled) setCustomers(res.data ?? [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isMedflow])

  // Memoize on timeRange: timeRangeToFrom() calls Date.now(), so computing it
  // inline would produce a new ISO string every render, churning useApiList's
  // filterKey and driving an unbounded refetch loop.
  const from = useMemo(() => timeRangeToFrom(timeRange), [timeRange])

  const { data, total, page, loading, error, setPage } = useApiList<InstrumentError>({
    path: '/instruments/errors',
    limit: 25,
    filters: {
      errorType: errorTypeFilter || undefined,
      from,
      organizationId: isMedflow ? orgFilter || undefined : undefined,
      siteId: siteFilter || undefined,
      deviceId: deviceFilter || undefined,
    },
  })

  const columns: readonly Column<InstrumentError>[] = [
    {
      key: 'reportedAt',
      header: 'Time',
      render: (r) => new Date(r.reportedAt).toLocaleString(),
      sortable: true,
    },
    ...(isMedflow
      ? [
          {
            key: 'organizationName' as const,
            header: 'Org',
            render: (r: InstrumentError) => (
              <>
                {r.organizationName ?? (
                  <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: colors.textMuted }}>
                    {r.organizationId}
                  </span>
                )}{' '}
                {r.organizationIsArchived ? <StatusBadge status="inactive" label="archived" /> : null}
              </>
            ),
            sortable: true,
          },
        ]
      : []),
    {
      key: 'deviceId',
      header: 'Device',
      render: (r) => r.deviceName ?? (r.udmDeviceId != null ? `Device #${r.udmDeviceId}` : (r.deviceId ?? '—')),
    },
    {
      key: 'siteId',
      header: 'Site',
      render: (r) => r.siteName ?? (r.udmSiteId != null ? `Site #${r.udmSiteId}` : (r.siteId ?? '—')),
    },
    {
      key: 'errorType',
      header: 'Error Type',
      render: (r) => <StatusBadge status={errorTypeBadgeStatus(r.errorType)} label={errorTypeLabel(r.errorType)} />,
      sortable: true,
    },
    {
      key: 'message',
      header: 'Message',
      render: (r) => (
        <span
          title={r.message}
          style={{
            display: 'block',
            maxWidth: '400px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {r.message}
        </span>
      ),
    },
    {
      key: 'udmVersion',
      header: 'UDM Version',
      render: (r) => r.udmVersion ?? '—',
    },
  ]

  const filterBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.35rem 0.75rem',
    border: `1px solid ${active ? colors.accent : colors.border}`,
    borderRadius: '4px',
    background: active ? '#eef4ff' : '#fff',
    color: active ? colors.accent : colors.text,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
  })

  const selectStyle: React.CSSProperties = {
    padding: '0.35rem 0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '12px',
    background: '#fff',
    color: colors.text,
    cursor: 'pointer',
  }

  const totalPages = Math.ceil(total / 25)

  return (
    <div>
      <PageHeader title="Instrument Error Log" subtitle={`${total} error${total === 1 ? '' : 's'} found`} />

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: colors.textMuted }}>Error type:</span>
          {(['', 'parse_error', 'rejected_message', 'comm_failure', 'config_error'] as const).map((et) => (
            <button
              key={et || 'all'}
              type="button"
              onClick={() => setErrorTypeFilter(et)}
              style={filterBtnStyle(errorTypeFilter === et)}
            >
              {et ? errorTypeLabel(et) : 'All'}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: 'auto' }}>
          <span style={{ fontSize: '12px', color: colors.textMuted }}>Time range:</span>
          <select value={timeRange} onChange={(e) => setTimeRange(e.target.value as TimeRange)} style={selectStyle}>
            <option value="1h">Last 1 hour</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="">All time</option>
          </select>
        </div>
      </div>

      {isMedflow && (
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            padding: '0.6rem 0.75rem',
            background: '#f9fafb',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
          }}
          data-testid="medflow-triage-filters"
        >
          <span style={{ fontSize: '12px', color: colors.textMuted, fontWeight: 600 }}>Triage:</span>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: colors.textMuted }}>Org:</span>
            <select
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              style={{ ...selectStyle, minWidth: '180px' }}
              data-testid="org-filter"
            >
              <option value="">All orgs</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: colors.textMuted }}>Site ID:</span>
            <input
              type="text"
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              placeholder="UUID"
              style={{ ...selectStyle, minWidth: '180px', fontFamily: 'monospace' }}
              data-testid="site-filter"
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: colors.textMuted }}>Device ID:</span>
            <input
              type="text"
              value={deviceFilter}
              onChange={(e) => setDeviceFilter(e.target.value)}
              placeholder="UUID"
              style={{ ...selectStyle, minWidth: '180px', fontFamily: 'monospace' }}
              data-testid="device-filter"
            />
          </div>
          {(orgFilter || siteFilter || deviceFilter) && (
            <button
              type="button"
              onClick={() => {
                setOrgFilter('')
                setSiteFilter('')
                setDeviceFilter('')
              }}
              style={{
                ...selectStyle,
                color: colors.accent,
                border: `1px solid ${colors.accent}`,
              }}
              data-testid="clear-triage-filters"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            emptyMessage="No instrument errors match the current filter."
          />
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={filterBtnStyle(false)}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: colors.textMuted, alignSelf: 'center' }}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                style={filterBtnStyle(false)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
